/**
 * Seller-display derivation for the inline chat quote `seller_match` cards.
 *
 * The backend `seller_match` card (`apps/api/garden/chat/orchestrator.py`
 * `_seller_cards_for_material`) carries only `seller_id` + `business_name` +
 * `price_kgs` + `unit` + `cheapest`. The design `SellerMiniCard`
 * (`docs/GardEn/handoff-2026-06/garden/screens-chat.jsx`) also shows an initials
 * Avatar on a coloured disc plus a rating/district line — fields the lean card
 * payload deliberately omits ("resolved front-end-side from the seller-profile
 * surface"). The rating/district are fetched from the public seller finder
 * (`useSellerDirectory`); the avatar initials + colour are DERIVED here.
 *
 * This mirrors `features/dashboard/seller-display.ts` (same palette + hash) so a
 * given seller renders the SAME disc colour across the dashboard, the materials
 * surface, and the chat seller cards. The palette is the fixture avatar colours
 * (`docs/GardEn/handoff-2026-06/garden/data.jsx` `GARDEN_SELLERS[].color`).
 * Colour is DATA, not chrome — the Avatar primitive documents `color` as the
 * intentional exception to the token-only rule — so a literal here is correct.
 */

/** Fixture avatar colours (data.jsx GARDEN_SELLERS colours) — a stable
 * deterministic palette for derived seller discs. */
const SELLER_AVATAR_COLORS: readonly string[] = [
  "#1F5D5E",
  "#C97B5E",
  "#566F4D",
  "#3A4A6B",
  "#A24A33",
  "#B07729",
  "#2D5566",
];

/** Stable non-negative hash of a string (djb2-ish), for deterministic palette
 * selection from a seller id. */
function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return hash >>> 0;
}

/**
 * Up-to-two-character uppercase initials from a business name. Takes the first
 * letter of the first two whitespace-separated words (e.g. "Ак-Тилек плитка" →
 * "АП"), or the first two characters of a single-word name. Returns "" for an
 * empty name (the Avatar still renders its disc).
 */
export function sellerInitials(businessName: string): string {
  const trimmed = businessName.trim();
  if (trimmed === "") return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  const letters =
    words.length >= 2
      ? `${words[0]?.[0] ?? ""}${words[1]?.[0] ?? ""}`
      : trimmed.slice(0, 2);
  return letters.toUpperCase();
}

/** Deterministic avatar colour for a seller, keyed on its stable id so the
 * same seller always gets the same disc colour across surfaces/sessions. */
export function sellerColor(sellerId: string): string {
  const index = hashString(sellerId) % SELLER_AVATAR_COLORS.length;
  return SELLER_AVATAR_COLORS[index] ?? SELLER_AVATAR_COLORS[0] ?? "#1F5D5E";
}
